import dayjs from 'dayjs'
import { z } from 'zod'
import { isValidUsMobileNumber } from '@/features/account/lib/account-phone-utils'

export const accountSettingsSchema = z.object({
  firstName: z
    .string()
    .trim()
    .refine((v) => v.length > 0, 'First name is required')
    .refine((v) => v.length >= 2, 'First name must be at least 2 characters')
    .refine((v) => v.length <= 50, 'First name must be at most 50 characters'),
  lastName: z
    .string()
    .trim()
    .refine((v) => v.length > 0, 'Last name is required')
    .refine((v) => v.length >= 2, 'Last name must be at least 2 characters')
    .refine((v) => v.length <= 50, 'Last name must be at most 50 characters'),
  email: z.string().email(),
  phone: z
    .string()
    .trim()
    .min(1, 'Phone number is required')
    .refine(isValidUsMobileNumber, 'Please enter a valid phone number'),
  dateOfBirth: z
    .string()
    .min(1, 'Date of birth is required')
    .refine((v) => dayjs(v).isValid(), 'Date of birth is required')
    .refine((v) => !dayjs(v).isAfter(dayjs(), 'day'), 'Date cannot be in the future')
    .refine(
      (v) => dayjs().diff(dayjs(v), 'year') >= 18,
      'You must be at least 18 years old to participate',
    ),
  gender: z.string().min(1, 'Gender is required'),
  maritalStatus: z.string().min(1, 'Marital status is required'),
  state: z.string().min(1, 'State is required'),
  city: z.string().min(1, 'City is required'),
  householdSize: z.string().min(1, 'Household size is required'),
  numberOfChildren: z.string().min(1, 'Number of children is required'),
  education: z.string().min(1, 'Education level is required'),
  employment: z.string().min(1, 'Employment status is required'),
  industry: z.string().min(1, 'Field of Work is required'),
  housingStatus: z.string().min(1, 'Housing Status is required'),
})

export type AccountSettingsFormData = z.infer<typeof accountSettingsSchema>

export const passwordChangeSchema = z
  .object({
    newPassword: z.string().min(8, 'Password must be at least 8 characters'),
    confirmPassword: z.string().min(1, 'Confirm your new password'),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  })

export type PasswordChangeFormData = z.infer<typeof passwordChangeSchema>
